/**
 * RescheduleForm — <MonthPicker> + grilla de horarios, inline, usado dentro de
 * AppointmentModal (terapeuta) y ClinicAppointmentModal (director) para
 * reagendar una consulta.
 *
 * Al tocar un día, la grilla de horarios se trae sola a la vista (scrollIntoView
 * + doble rAF, mismo patrón que CalendarSelectorImproved del booking) y se
 * atenúa mientras se consulta disponibilidad.
 *
 * NO reusa CalendarSelectorImproved: ese hace fetch de disponibilidad en vivo
 * contra CalendarServiceProduction (pensado para el booking público, demasiado
 * pesado para un picker de modal). Acá la disponibilidad se calcula con un
 * fetch directo a get_booked_slots_for_date (FUNC-077) que pinta solo el
 * choque DIRECTO contra otra consulta del mismo terapeuta — el caso dominante
 * que el usuario reportó ("las horas que me ofrecen son todas"). La validación
 * real de choque (consultorio lleno, transición de modalidad) la hace la RPC
 * reschedule_consultation_terapeuta server-side (check_slot_availability); si
 * el horario elegido choca por una regla que este picker no chequea, el error
 * cae genérico post-submit, sin regresión respecto al comportamiento previo.
 */
import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase/client';
import { toSantiagoISO } from '../../lib/utils/dateUtils';
import MonthPicker, { todayDateStr } from '../ui/MonthPicker';

// 09:00–20:00 cada 30 min — mismo rango que el booking público
// (CalendarServiceProduction.DAY_HOURS, último inicio 20:00). Antes cortaba en
// 19:30 y el staff no podía agendar a las 20:00 (FUNC-126).
const TIME_SLOTS: string[] = [];
for (let h = 9; h <= 20; h++) {
  for (const m of [0, 30]) {
    if (h === 20 && m > 0) break;
    TIME_SLOTS.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
  }
}

interface BookedSlot {
  fecha_hora: string;
  duracion: number;
}

interface RescheduleFormProps {
  /** Fecha actual de la consulta, YYYY-MM-DD (Santiago) — precarga el picker. */
  initialDate: string;
  /** Hora actual de la consulta, HH:MM (Santiago) — precarga el picker. */
  initialTime: string;
  /** Terapeuta dueño de la consulta — usado para calcular horarios ocupados. */
  therapistId: string;
  /** Duración de la consulta en minutos — usada para el cálculo de choque del nuevo horario. */
  duration: number;
  loading: boolean;
  errorMessage: string | null;
  onConfirm: (newFechaHoraISO: string) => void;
  onCancel: () => void;
}

const RescheduleForm: React.FC<RescheduleFormProps> = ({
  initialDate,
  initialTime,
  therapistId,
  duration,
  loading,
  errorMessage,
  onConfirm,
  onCancel,
}) => {
  const [fecha, setFecha] = useState(initialDate || todayDateStr());
  const [hora, setHora] = useState(TIME_SLOTS.includes(initialTime) ? initialTime : TIME_SLOTS[0]);
  const [busySlots, setBusySlots] = useState<Set<string>>(new Set());
  const [checkingAvailability, setCheckingAvailability] = useState(false);

  const slotSectionRef = useRef<HTMLDivElement>(null);
  const lastScrolledFecha = useRef(fecha);

  useEffect(() => {
    if (!therapistId || !fecha) {
      setBusySlots(new Set());
      return;
    }

    let cancelled = false;
    setCheckingAvailability(true);

    (async () => {
      const { data, error } = await supabase
        .rpc('get_booked_slots_for_date', { p_therapist_id: therapistId, p_date: fecha }) as {
          data: BookedSlot[] | null;
          error: unknown;
        };

      if (cancelled) return;

      if (error || !data) {
        console.error('[RescheduleForm] Error al verificar disponibilidad');
        setBusySlots(new Set());
        setCheckingAvailability(false);
        return;
      }

      // Excluir la propia consulta que se está reagendando — comparte
      // fecha_hora exacta con initialDate+initialTime, así que aparecería
      // como "ocupada" contra sí misma si no se filtra (misma razón por la
      // que reschedule_consultation_terapeuta usa p_exclude_consultation_id
      // en check_slot_availability).
      const selfStartMs = new Date(toSantiagoISO(initialDate, initialTime)).getTime();

      const occupied = new Set<string>();
      for (const slot of TIME_SLOTS) {
        const slotStartMs = new Date(toSantiagoISO(fecha, slot)).getTime();
        const slotEndMs = slotStartMs + duration * 60000;

        const clashes = data.some(row => {
          const rowStartMs = new Date(row.fecha_hora).getTime();
          if (rowStartMs === selfStartMs) return false;
          const rowEndMs = rowStartMs + row.duracion * 60000;
          return slotStartMs < rowEndMs && rowStartMs < slotEndMs;
        });

        if (clashes) occupied.add(slot);
      }

      setBusySlots(occupied);
      setCheckingAvailability(false);
    })();

    return () => { cancelled = true; };
  }, [therapistId, fecha, duration, initialDate, initialTime]);

  useEffect(() => {
    if (busySlots.size > 0 && busySlots.has(hora)) {
      setHora('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busySlots]);

  // Al elegir un día distinto, traer la sección de horarios a la vista dentro
  // del modal (que es overflow-y:auto). Doble rAF: scrollIntoView en el mismo
  // tick del click se descarta sin moverse — mismo problema y misma solución
  // que en CalendarSelectorImproved del booking. El ref de la última fecha
  // scrolleada evita el yank en el mount inicial y en el doble-invoke de
  // effects de StrictMode.
  useEffect(() => {
    if (lastScrolledFecha.current === fecha) return;
    lastScrolledFecha.current = fecha;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        slotSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    });
  }, [fecha]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!hora) return;
    onConfirm(toSantiagoISO(fecha, hora));
  };

  return (
    <form className="reschedule-panel" onSubmit={handleSubmit}>
      <div className="reschedule-field">
        <span className="reschedule-field-label">Nueva fecha</span>
        <MonthPicker value={fecha} onChange={setFecha} ariaLabel="Elegí la nueva fecha" />
      </div>

      <div className="reschedule-field" ref={slotSectionRef}>
        <span className="reschedule-field-label" id="reschedule-hora">
          Nueva hora{checkingAvailability ? ' · verificando disponibilidad…' : ''}
        </span>
        <div
          key={fecha}
          className={`reschedule-slot-grid${checkingAvailability ? ' is-loading' : ''}`}
          role="radiogroup"
          aria-labelledby="reschedule-hora"
          aria-busy={checkingAvailability}
        >
          {TIME_SLOTS.map(t => {
            const isOcupado  = busySlots.has(t);
            const isSelected = hora === t;
            return (
              <button
                key={t}
                type="button"
                className={`reschedule-slot ${isSelected ? 'selected' : ''} ${isOcupado ? 'occupied' : ''}`}
                disabled={isOcupado || checkingAvailability}
                aria-pressed={isSelected}
                onClick={() => setHora(t)}
              >
                {t}
              </button>
            );
          })}
        </div>
      </div>

      {errorMessage && (
        <p className="reschedule-error" role="alert">{errorMessage}</p>
      )}

      <div className="reschedule-actions">
        <button type="button" className="reschedule-btn reschedule-btn--secondary" onClick={onCancel} disabled={loading}>
          Volver
        </button>
        <button type="submit" className="reschedule-btn reschedule-btn--primary" disabled={loading || !hora}>
          {loading ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-check" />}
          Confirmar nuevo horario
        </button>
      </div>
    </form>
  );
};

export default RescheduleForm;
